// The composer's compose-feature half, as ONE hook the shared Composer calls (anchored edits there
// stay a few lines): the "/" command strip and execution, the "~" channel strip, the one-shot
// message priority, the ephemeral/error notice above the box, and ↑-to-edit.
//
// Ported from ChannelViewModel.swift (prefetchSlashCommandsIfNeeded, sendMessage's command
// branch, messagePriority) and MacConversationView (SlashCommandStripView / channel-link strip /
// EphemeralNoticeView). Web-only on top: goto_location (a browser has places to go), the
// server's other public channels in the "~" strip, and ↑ in an empty box.
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { data } from '../../../bridge/client';
import type { SendExtras } from '../../../bridge/protocol';
import { ChannelLinks, type ChannelLinkActive, type ChannelLinkTarget } from '../../../core/channelLinks';
import { ChannelSuggestions, type ChannelSuggestion } from '../../../core/channelSuggestions';
import { CommandOutcome } from '../../../core/commandOutcome';
import { MessagePriority, type Level } from '../../../core/messagePriority';
import * as SlashCommandComposer from '../../../core/slashCommandComposer';
import type { ComposeConfig } from '../../../data/features/compose';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { MessageBody } from '../../conversation/MessageBody';
import { parseRoute } from '../../router';
import { ShellContext, type Shell } from '../../shell/ShellContext';
import { requestEdit, useChannelCatalogue } from './composeSignals';
import { PriorityButton, PriorityLine } from './Priority';
import './compose.css';

export type ComposeSuggestion =
  | { kind: 'command'; items: SlashCommandComposer.SlashCommand[] }
  | { kind: 'channel'; active: ChannelLinkActive; items: ChannelSuggestion[] };

type Notice = { tone: 'ephemeral' | 'error'; text: string } | null;

const REMOTE_DEBOUNCE_MS = 200;
let sessionConfig: ComposeConfig | null = null;

/** Routes an in-app path through the shell (surfaces never touch history themselves). */
async function openInApp(shell: Shell, path: string, search: string): Promise<boolean> {
  const route = parseRoute(path, search);
  const teamId = (name: string) => shell.teams.find((x) => x.name === name)?.id ?? shell.team.id;
  switch (route.kind) {
    case 'channel': {
      const id = await data.call('channelIdByName', teamId(route.team), route.channelName);
      if (!id) return false;
      shell.openChannel(id);
      return true;
    }
    case 'dm': {
      const user = await data.call('resolveUserByUsername', route.username);
      if (!user) return false;
      shell.openDirectMessage(user.id);
      return true;
    }
    case 'permalink':
      shell.jumpToPost(route.postId);
      return true;
    case 'destination': {
      const d = route.destination;
      if (d === 'search') shell.openSearch(route.param ?? '');
      else if (d === 'threads' || d === 'activity' || d === 'saved' || d === 'drafts') shell.openDestination(d);
      else if (d === 'settings') shell.openSettings(route.param ?? null);
      return true;
    }
    default:
      return false;
    case 'home':
      return true;
  }
}

export function useComposeFeatures(o: {
  channelId: string;
  rootId: string | null;
  text: string;
  cursor: number;
  textarea: RefObject<HTMLTextAreaElement | null>;
  /** Puts text in the box with the caret at `caret` (the Composer's own `place`). */
  place(value: string, caret: number): void;
}) {
  const shell = useContext(ShellContext);
  const teamId = shell?.team.id ?? '';
  const catalogue = useChannelCatalogue();
  const [commands, setCommands] = useState<SlashCommandComposer.SlashCommand[]>([]);
  const commandsFor = useRef('');
  const [remote, setRemote] = useState<{ query: string; items: ChannelLinkTarget[] }>({ query: '', items: [] });
  const [priority, setPriority] = useState<Level | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [config, setConfig] = useState<ComposeConfig | null>(sessionConfig);
  const placeRef = useRef(o.place);
  placeRef.current = o.place;

  useEffect(() => {
    if (sessionConfig) return;
    let alive = true;
    void data.call('composeConfig').then((c) => {
      sessionConfig = c;
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  // The catalogue is fetched the first time a draft starts with "/" — not at open: most
  // conversations never type a command (prefetchSlashCommandsIfNeeded).
  const wantsCommands = o.text.startsWith('/');
  useEffect(() => {
    if (!wantsCommands || !teamId || commandsFor.current === teamId) return;
    commandsFor.current = teamId;
    void data.call('slashCommands', teamId).then(setCommands, () => undefined);
  }, [wantsCommands, teamId]);

  // The server's other public channels for the "~" query, debounced; the joined tier paints first.
  const channelQuery = ChannelLinks.active(o.text, o.cursor)?.query ?? null;
  useEffect(() => {
    if (channelQuery === null || !teamId) return;
    const id = setTimeout(() => {
      void data.call('channelAutocomplete', teamId, channelQuery).then((items) => setRemote({ query: channelQuery, items }));
    }, REMOTE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [channelQuery, teamId]);

  /** The strip for the caret, or null — called by the Composer AFTER @ and : had their turn. */
  const suggestion = useCallback(
    (text: string, cursor: number): ComposeSuggestion | null => {
      const query = SlashCommandComposer.activeQuery(text);
      if (query !== null) {
        // A trigger typed out in full needs no strip: Enter must RUN "/away", not complete it.
        if (commands.some((c) => c.trigger.toLowerCase() === query.toLowerCase())) return null;
        const items = SlashCommandComposer.filter(commands, query);
        return items.length ? { kind: 'command', items } : null;
      }
      const active = ChannelLinks.active(text, cursor);
      if (active) {
        const others = remote.query === active.query || active.query.startsWith(remote.query) ? remote.items : [];
        const items = ChannelSuggestions.suggestions(catalogue.joined, others, active.query);
        return items.length ? { kind: 'channel', active, items } : null;
      }
      return null;
    },
    [commands, remote, catalogue.joined],
  );

  const accept = useCallback((s: ComposeSuggestion, index: number, text: string, cursor: number): { text: string; cursor: number } => {
    if (s.kind === 'command') {
      const value = SlashCommandComposer.apply(s.items[index].trigger);
      return { text: value, cursor: value.length };
    }
    return ChannelLinks.apply({ text, cursor, active: s.active, slug: s.items[index].slug });
  }, []);

  const renderItems = (s: ComposeSuggestion, highlight: number, pick: (i: number) => void): ReactNode => {
    if (s.kind === 'command') {
      return s.items.map((c, i) => (
        <button key={c.trigger} className="item compose-command" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), pick(i))}>
          <span className="trigger-line">
            <span className="trigger">/{c.trigger}</span>
            {c.autoCompleteHint ? <span className="hint">{c.autoCompleteHint}</span> : null}
          </span>
          {c.autoCompleteDesc ? <span className="desc">{c.autoCompleteDesc}</span> : null}
        </button>
      ));
    }
    const out: ReactNode[] = [];
    s.items.forEach((c, i) => {
      if (!c.joined && (i === 0 || s.items[i - 1].joined)) {
        out.push(
          <div key="__others" className="divider">
            {t('web.compose.channels.other')}
          </div>,
        );
      }
      out.push(
        <button key={c.channelId} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), pick(i))}>
          <span className="compose-glyph">
            <Icon name={c.isPrivate ? 'lock' : 'hash'} size={16} />
          </span>
          <span>{c.displayName}</span>
          <span className="sub">~{c.slug}</span>
        </button>,
      );
    });
    return out;
  };

  const go = useCallback(
    async (location: string) => {
      const target = CommandOutcome.gotoTarget(location, window.location.origin, sessionConfig?.siteUrl ?? '');
      if (!target) return;
      if (target.kind === 'external') {
        window.open(target.url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (shell && !(await openInApp(shell, target.path, target.search))) setNotice({ tone: 'error', text: t('web.compose.command.failed') });
    },
    [shell],
  );

  /**
   * A leading "/" is a command attempt (shared/API.md §15) — unless it carries files: a command
   * cannot take one, so the reader plainly meant a message (ChannelViewModel.sendMessage).
   * Returns true when the message was taken as a command (the Composer then clears the box).
   */
  const trySend = useCallback(
    (message: string, extras: SendExtras): boolean => {
      if (!SlashCommandComposer.isCommandMessage(message) || (extras.fileIds?.length ?? 0) > 0 || !teamId) return false;
      setNotice(null);
      setRunning(message.split(/\s/)[0]);
      void data.call('executeCommand', { teamId, channelId: o.channelId, rootId: o.rootId, command: message }).then(
        (r) => {
          setRunning(null);
          if (r.kind === 'executed') {
            if (r.ephemeralText && !r.ephemeralShown) setNotice({ tone: 'ephemeral', text: r.ephemeralText });
            if (r.gotoLocation) void go(r.gotoLocation);
            return;
          }
          // Unknown trigger or refusal: the SERVER's sentence (it says how to send a literal "/"),
          // and the words go back in the box if the reader has not started something else.
          setNotice({ tone: 'error', text: r.message || t('web.compose.command.failed') });
          const el = o.textarea.current;
          if (el && el.value === '') placeRef.current(message, message.length);
        },
        () => {
          setRunning(null);
          setNotice({ tone: 'error', text: t('web.compose.command.failed') });
        },
      );
      return true;
    },
    [teamId, o.channelId, o.rootId, o.textarea, go],
  );

  /** Merged into the send's extras; the Composer calls `sent()` after. One-shot, like the Mac. */
  const extras = useCallback((): SendExtras => {
    const wire = MessagePriority.wire(priority);
    return wire ? { priority: wire } : {};
  }, [priority]);
  const sent = useCallback(() => {
    setPriority(null);
    setNotice(null);
  }, []);

  /** ↑ in an empty box edits your last message here; true when the key was taken. */
  const keyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, text: string): boolean => {
      if (e.key !== 'ArrowUp' || text !== '' || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey || e.nativeEvent.isComposing) return false;
      e.preventDefault();
      const returnFocus = o.textarea.current;
      void data.call('lastEditablePost', o.channelId, o.rootId).then((postId) => {
        if (postId) requestEdit(postId, !!o.rootId, returnFocus);
      });
      return true;
    },
    [o.channelId, o.rootId, o.textarea],
  );

  const priorityAvailable = config ? config.postPriority : true;
  const toolbar = priorityAvailable ? <PriorityButton value={priority} onChange={setPriority} /> : null;

  const above: ReactNode = (
    <>
      {running ? <div className="compose-running">{tf('web.compose.command.running', running)}</div> : null}
      {notice ? (
        <div className={`compose-notice ${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
          <div className="text">
            {notice.tone === 'ephemeral' ? <div className="only-you">{t('channel.ephemeral.onlyYou')}</div> : null}
            {notice.tone === 'ephemeral' ? <MessageBody message={notice.text} ctx={NOTICE_CTX} /> : notice.text}
          </div>
          <button type="button" onClick={() => setNotice(null)}>
            {t('channel.ephemeral.dismiss')}
          </button>
        </div>
      ) : null}
    </>
  );

  /** Goes INSIDE the box, above the field (the Mac's composerFieldIdle line). */
  const fieldLine = priority && priorityAvailable ? <PriorityLine value={priority} onClear={() => setPriority(null)} /> : null;

  return useMemo(
    () => ({ suggestion, accept, renderItems, trySend, extras, sent, keyDown, toolbar, above, fieldLine }),
    // renderItems/toolbar/above are cheap render helpers; recomputed with the state they read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestion, accept, trySend, extras, sent, keyDown, priority, priorityAvailable, notice, running],
  );
}

const NOTICE_CTX = { knownUsernames: new Set<string>(), currentUsername: null, customEmoji: {} };
